/**
 * This component is for the second line of the header
 * On desktop it contains logo, search section, and customer nav
 * On mobile it is hamburger for menu, logo, cart
 * Some of the mobile styles are done on the CustomerNav component (to handle showing cart but not other sections)
 */

import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { Button } from '@components/Button';
import { CartItemsCountButton } from '@components/Button/CartItemsCountButton';
import { Container } from '@components/Container';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Logo } from '@components/Header/Logo';
import { Icons } from '@components/Icons/Icons';
import { DropdownLogin } from '@components/LoginForm/DropdownLogin';
import { Autocomplete } from '@components/Search';
import { TooltipWrapper } from '@components/Tooltip/Tooltip';
import { useCustomer } from '@hooks/customer/useCustomer';
import { type ReactElement, useContext } from 'react';

const ICON_SIZE = '1.75rem';

function CustomerNav(): ReactElement {
  const { customer } = useCustomer();
  const { quickCartIsOpen, setQuickCartIsOpen } =
    useContext<UIContextInterface>(UIContext);

  const handleCartEvent = () => {
    setQuickCartIsOpen(!quickCartIsOpen);
  };

  return (
    <div className="flex w-fit gap-0 sm:gap-4 order-4">
      <Button buttonStyle="primary" className="hidden lg:flex" href="/goal">
        Start your journey
      </Button>
      <TooltipWrapper text="Rewards">
        <Button
          aria-label="Rewards"
          href={customer ? '/account/rewards' : '/rewards'}
          className="hidden tiny:flex relative h-10 w-10 self-center overflow-visible px-1 text-xs ring-grey-mid ring-offset-2 hover:scale-110 hover:rounded-full hover:bg-white hover:text-black hover:ring-1 hover:ring-grey-mid disabled:text-grey-mid dark:hover:bg-grey-dark dark:hover:text-white dark:disabled:bg-transparent"
          buttonStyle="tertiary"
          icon={<Icons.Trophy.Outline size={ICON_SIZE} />}
        />
      </TooltipWrapper>
      <DropdownLogin />
      <CartItemsCountButton handleCartEvent={handleCartEvent} />
    </div>
  );
}

export function Quicklinks() {
  const { setMenuIsOpen } = useContext<UIContextInterface>(UIContext);

  const handleMenuEvent = () => {
    setMenuIsOpen(true);
  };

  return (
    <div className="min-h-[76px] bg-grey-light dark:bg-black xl:min-h-[76px]">
      <Container>
        <div className="flex items-center justify-between gap-0 px-0 py-2.5">
          {/* Logo */}
          <div className="spacer order-2 grow md:hidden"></div>
          <Logo />
          {/* Menu and Search */}
          <div className="mx-0 my-0 flex items-center gap-0 tiny:gap-2 sm:mx-2.5 sm:gap-4">
            <TooltipWrapper text="Menu">
              <Button
                onClick={handleMenuEvent}
                aria-label="Menu"
                className="relative w-10 ring-grey-mid ring-offset-2 hover:scale-110 hover:rounded-full hover:bg-white hover:text-black hover:ring-1 hover:ring-grey-mid dark:hover:bg-grey-dark dark:hover:text-white"
                buttonStyle="tertiary"
                icon={<Icons.Menu size={ICON_SIZE} />}
              />
            </TooltipWrapper>
            {/* Add Search Icon for under md */}
          </div>

          <div className="spacer grow order-3 md:hidden"></div>

          {/* Desktop Search */}
          <BulkErrorBoundary location="Header/Quicklinks/Autocomplete">
            <div className="mx-auto md:order-2">
              <Autocomplete />
            </div>
          </BulkErrorBoundary>

          {/* Quick Nav */}
          <CustomerNav />
        </div>
      </Container>
    </div>
  );
}
